import {
    cssX,
    FCAdditionalProps,
    getScaledPixels,
    ID,
    includesInLC,
    markMatch,
    SearchBoxNoResults,
    startsFromInLC, useWindowSizeDebounced
} from "@datassential/platform-ui-lib";
import SelectComponent, { SelectComponentProps } from "./SelectComponent";
import { IOption } from "@models";
import { CSSProperties, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import cn from "classnames";
import { Toggle, ToggleType } from "@components/Toggle";
import { FiSearch } from "react-icons/fi";
import TextInput from "@components/TextInput";
import { toggleInArray } from "@core/utils/array";
import { AutoSizer } from "react-virtualized";
import { FixedSizeList } from "react-window";
import { FaCheck } from "react-icons/fa";

const Item = ({
    isSelected,
    onClick,
    hasToggle,
    id,
    title: rawTitle,
    testId,
    style,
    isMulti = false,
    addCheckMarks = false,
}: Omit<FCAdditionalProps, 'id'> & {
    isSelected: boolean;
    onClick: (id: any) => void;
    hasToggle?: boolean;
    id: any;
    title: ReactNode;
    isMulti?: boolean;
    addCheckMarks?: boolean;
}) => {
    const title = useMemo(
        () => {
            if (isSelected && addCheckMarks) {
                return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        {rawTitle}<FaCheck className="SelectComponent__check-mark"/>
                    </div>
                );
            }

            return rawTitle;
        },
        [rawTitle, isSelected, addCheckMarks]
    );

    return (
        <div
            className={cn(cssX("Dropdown__row"))}
            data-testid={testId}
            style={style}
        >
            <div
                className={cn(
                    cssX('Dropdown__item'),
                    'SelectComponent__item',
                    isSelected && 'active-accessible',
                )}
                onClick={() => onClick(id)}
            >
                {hasToggle ? (
                    <Toggle
                        type={isMulti ? ToggleType.Checkbox : ToggleType.Radio}
                        isChecked={isSelected}
                        onChange={() => null}
                        label={title}
                    />
                ) : title}
            </div>
        </div>
    );
};

type Props<I extends ID, T = string> = FCAdditionalProps & {
    value: I[];
    setValue: (value: I[]) => void;
    options: IOption<I, T>[];

    label?: SelectComponentProps["label"];
    placeholder?: string;
    modifiers?: SelectComponentProps['modifiers'];
    disabled?: boolean;

    dropdownProps?: Omit<SelectComponentProps['dropdownProps'], 'isOpened' | 'groups' | 'columns'>;
    fieldProps?: SelectComponentProps['fieldProps'];

    hasToggle?: boolean;
    hasSearch?: boolean;
    isMulti?: boolean;
    allowUnselect?: boolean;
    addCheckMarks?: boolean;
    moveSelectedToTop?: boolean;
    hideSelected?: boolean;

    onOpenChange?: SelectComponentProps['onOpenChange'];

    isVirtualized?: boolean;
    virtualizedItemHeight?: number;
    virtualizeContainerMaxHeight?: number;
};

const Select = <I extends ID, T = string>({
    value,
    setValue,
    options,
    placeholder,
    label,
    hasToggle = false,
    addCheckMarks = false,
    hasSearch = false,
    moveSelectedToTop = false,
    hideSelected = false,
    dropdownProps,
    fieldProps,
    allowUnselect,
    isMulti,
    isVirtualized,
    virtualizedItemHeight: virtualizedItemHeightRaw = 38,
    virtualizeContainerMaxHeight: virtualizeContainerMaxHeightRaw = 350,
    disabled,
    ...rest
}: Props<I, T>) => {
    const [isOpened, setIsOpened] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [wWidth] = useWindowSizeDebounced();

    const virtualizedItemHeight = useMemo(
        () => getScaledPixels(virtualizedItemHeightRaw, wWidth),
        [virtualizedItemHeightRaw, wWidth]
    );
    const virtualizeContainerMaxHeight = useMemo(
        () => getScaledPixels(virtualizeContainerMaxHeightRaw, wWidth),
        [virtualizeContainerMaxHeightRaw, wWidth]
    );

    useEffect(() => {
        if (!isOpened) {
            setSearchValue("");
        }
    }, [isOpened]);

    const filterBySearch = useCallback(
        (options: IOption<I, T>[]) => {
            if (searchValue.trim() === "") {
                return options;
            }
            return [
                ...options.filter((option) => {
                    if (typeof option.title === 'string') {
                        return startsFromInLC(option.title, searchValue.trim());
                    }
                    return false;
                }),
                ...options.filter((option) => {
                    if (typeof option.title === 'string') {
                        return !startsFromInLC(option.title, searchValue.trim())
                            && includesInLC(option.title, searchValue.trim());
                    }
                    return false;
                })
            ];
        },
        [searchValue]
    );

    const selectedOptions = useMemo(
        () => {
            return filterBySearch(options.filter((option) => value.includes(option.id)));
        },
        [options, value, filterBySearch]
    );

    const availableOptions = useMemo(
        () => {
            return filterBySearch(
                moveSelectedToTop || hideSelected ? options.filter((option) => !value.includes(option.id)) : options
            );
        },
        [options, value, filterBySearch, moveSelectedToTop, hideSelected]
    );

    const thumbnailText = useMemo(
        () => {
            if (selectedOptions.length === 0) {
                return placeholder
            } else if (selectedOptions.length === 1) {
                return `${selectedOptions[0].title}`;
            }
            return `${selectedOptions.length} selected`;
        },
        [selectedOptions, placeholder]
    );

    const onOptionChange = useCallback(
        (optionId: I) => {
            if (isMulti) {
                setValue(toggleInArray(value, optionId));
            } else {
                if (allowUnselect && value.includes(optionId)) {
                    setValue([]);
                } else {
                    setValue([optionId]);
                }
                setIsOpened(false);
            }
        },
        [isMulti, setValue, value, allowUnselect]
    );

    const getItemCommonProps = useCallback(
        (option: IOption<I, T>) => ({
            key: option.id,
            isSelected: value.includes(option.id),
            onClick: () => {
                onOptionChange(option.id);
            },
            id: option.id,
            title: typeof option.title === 'string' ? (
                <div dangerouslySetInnerHTML={{
                    __html: markMatch(option.title, searchValue.trim(), true)
                }}/>
            ) : option.title as ReactNode,
            isMulti,
            hasToggle,
            addCheckMarks,
        }),
        [value, onOptionChange, searchValue, isMulti, hasToggle, addCheckMarks]
    );

    const VirtualizedItem = useCallback(
        ({ index, style }: { index: number; style: CSSProperties }) => {
            const option = availableOptions[index];
            if (!option) return null;

            return (
                <Item
                    {...getItemCommonProps(option)}
                    data-testid={`blockVirtualizedSelectItem_${index}`}
                    style={style}
                />
            );
        },
        [availableOptions, getItemCommonProps]
    );

    const content = useMemo(
        () => {
            const mapOptionToItem = (option: IOption<I, T>, index: number) => (
                <Item
                    {...getItemCommonProps(option)}
                    className={cn(cssX("Dropdown__row"))}
                    testId={`blockSelect${value.includes(option.id) ? 'Active' : ''}DropdownItem_${index}`}
                />
            );

            return (
                <>
                    {hasSearch && (
                        <div className="SelectComponent__search">
                            <TextInput
                                placeholder="Search"
                                modifiers={["transparent"]}
                                prefix={<FiSearch/>}
                                value={searchValue}
                                setValue={setSearchValue}
                                autoFocus
                            />
                        </div>
                    )}
                    {moveSelectedToTop && !hideSelected && selectedOptions.length > 0 && (
                        <div className={cn(cssX("Dropdown__group"), "SelectComponent__group", 'is-separate')}>
                            {selectedOptions.map(mapOptionToItem)}
                        </div>
                    )}
                    {!isVirtualized && (
                        <div className={cn(cssX("Dropdown__group"), "SelectComponent__group")}>
                            {availableOptions.map(mapOptionToItem)}
                        </div>
                    )}
                    {isVirtualized && (
                        <div className={cn(cssX("Dropdown__group"), "SelectComponent__group")}>
                            <div style={{
                                height: Math.min(
                                    availableOptions.length * virtualizedItemHeight,
                                    virtualizeContainerMaxHeight
                                ),
                                overflow: 'hidden',
                            }}>
                                <AutoSizer>
                                    {({ width, height }) => (
                                        <FixedSizeList
                                            itemSize={virtualizedItemHeight}
                                            width={width}
                                            height={height}
                                            itemCount={availableOptions.length}
                                        >
                                            {VirtualizedItem}
                                        </FixedSizeList>
                                    )}
                                </AutoSizer>
                            </div>
                        </div>
                    )}
                    {availableOptions.length === 0 && (
                        <SearchBoxNoResults/>
                    )}
                </>
            );
        },
        [
            hasSearch,
            searchValue,
            setSearchValue,
            moveSelectedToTop,
            selectedOptions,
            availableOptions,
            isVirtualized,
            virtualizedItemHeight,
            virtualizeContainerMaxHeight,
            VirtualizedItem,
            value,
            hideSelected,
            getItemCommonProps
        ]
    );

    return (
        <SelectComponent
            {...rest}
            thumbnailText={thumbnailText}
            isFluidWidth
            isOpened={isOpened}
            setIsOpened={setIsOpened}
            label={label}
            fieldProps={fieldProps}
            className={cn(
                rest.className,
                disabled && 'is-disabled'
            )}
            dropdownProps={{
                ...dropdownProps,
                isOpened,
                columns: [{
                    id: 'id',
                    title: '',
                    items: [],
                    content,
                }],
            }}
        />
    );
};

export type SelectProps<I extends ID, T = string> = Props<I, T>;

export default Select;